import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { fromCanonicalKg, toCanonicalKg } from "@jackedlogs/shared";
import { writeErrorCode, type ConfirmFn, type DurableWriteResult } from "../seams";

export type WeightUnit = "kg" | "lb";

/** A MAX write, as the athlete meant it — set a value or clear it.
 *  `source` is always "tested" here: this editor is the athlete typing a
 *  number they achieved, never a derived estimate (the post-workout pill is
 *  the one that writes "derived"). */
export type MaxWrite =
  | {
      kind: "set";
      exerciseId: string;
      valueKg: number;
      source: "tested";
    }
  | { kind: "clear"; exerciseId: string };

/**
 * ⛔ Host seam for MAX writes specifically, NOT the generic `DurableWriteFn`,
 * and the distinction is a data-correctness one rather than a style choice.
 *
 * `/api/me/maxes/:slug` already has a dedicated mobile queue (`maxOutbox`),
 * because the post-workout "new MAX?" confirm writes there too. Routing this
 * editor onto the generic request queue instead would put TWO queues on the
 * same resource, and they drain concurrently with no ordering between them —
 * so an athlete who confirms a bump offline and then corrects it by hand
 * would get whichever request happened to resolve last, not their last
 * action. On a 1RM. `maxOutbox`'s own header anticipated this wiring and
 * asked for exactly this: reuse that queue.
 *
 * It lives here and not in `seams.ts` because that module is concept-neutral
 * by contract — it knows what a request is, never what a MAX is.
 */
export type MaxWriteFn = (op: MaxWrite) => Promise<DurableWriteResult>;

export type UseMaxEditorDeps = {
  writeMax: MaxWriteFn;
  /** The current { exerciseId → kg } map (to seed the input + gate Clear). */
  maxes: Map<string, number>;
  /** Re-fetch after a mutation so consumers see the new value immediately. */
  forceRefresh: () => Promise<unknown>;
  confirm: ConfirmFn;
};

// Edit orchestration for the Maxes surface — open/edit state + the
// PUT/DELETE mutations against /api/me/maxes/:id. Lifted from mobile's
// MaxesScreen so both mounts share one mutation path; the view stays
// presentational. Errors surface as either a localized invalid message or
// a raw `api_error_<status>` code (preserved from the mobile original).
export function useMaxEditor(deps: UseMaxEditorDeps) {
  const { t } = useTranslation();
  const { maxes, forceRefresh, confirm } = deps;

  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editUnit, setEditUnit] = useState<WeightUnit>("kg");
  const [editBusy, setEditBusy] = useState<"save" | "clear" | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const openEdit = useCallback(
    (slug: string) => {
      const current = maxes.get(slug);
      setEditingSlug(slug);
      setEditUnit("kg");
      setEditValue(
        current !== undefined ? String(fromCanonicalKg(current, "kg")) : "",
      );
      setEditError(null);
    },
    [maxes],
  );

  const closeEdit = useCallback(() => {
    setEditBusy((busy) => {
      if (busy === null) setEditingSlug(null);
      return busy;
    });
  }, []);

  const toggleUnit = useCallback(
    () => setEditUnit((u) => (u === "kg" ? "lb" : "kg")),
    [],
  );

  const save = useCallback(async () => {
    if (!editingSlug || editBusy !== null) return;
    const parsed = parseFloat(editValue);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setEditError(t("mobile.maxes.errInvalid"));
      return;
    }
    setEditBusy("save");
    setEditError(null);
    try {
      const valueKg = toCanonicalKg(parsed, editUnit);
      const result = await deps.writeMax({
        kind: "set",
        exerciseId: editingSlug,
        valueKg,
        source: "tested",
      });
      const code = writeErrorCode(result);
      if (result.outcome === "rejected") {
        setEditError(code ?? t("mobile.maxes.errInvalid"));
        return;
      }
      // sent or queued: the write is durable either way — work by default,
      // no distinct UI (offline-writes.md §IL SEAM DI SCRITTURA). A queued
      // write's forceRefresh legitimately fails offline; that is not an
      // error to surface, so it is swallowed here rather than caught below.
      await forceRefresh().catch(() => undefined);
      setEditingSlug(null);
    } finally {
      setEditBusy(null);
    }
  }, [editingSlug, editBusy, editValue, editUnit, deps, forceRefresh, t]);

  const clear = useCallback(async () => {
    if (!editingSlug || editBusy !== null) return;
    const ok = await confirm({
      title: t("mobile.maxes.clearTitle"),
      body: t("mobile.maxes.clearBody"),
      confirmLabel: t("mobile.maxes.clearConfirm"),
      cancelLabel: t("mobile.maxes.clearCancel"),
    });
    if (!ok) return;
    setEditBusy("clear");
    setEditError(null);
    try {
      const result = await deps.writeMax({
        kind: "clear",
        exerciseId: editingSlug,
      });
      const code = writeErrorCode(result);
      if (result.outcome === "rejected") {
        setEditError(code ?? t("mobile.maxes.errInvalid"));
        return;
      }
      await forceRefresh().catch(() => undefined);
      setEditingSlug(null);
    } finally {
      setEditBusy(null);
    }
  }, [editingSlug, editBusy, confirm, deps, forceRefresh, t]);

  return {
    editingSlug,
    editValue,
    editUnit,
    editBusy,
    editError,
    canClear: maxes.has(editingSlug ?? ""),
    setEditValue,
    openEdit,
    closeEdit,
    toggleUnit,
    save,
    clear,
  };
}
